// @ts-nocheck
import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Card, CardContent, CardMedia, Typography, Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { DateUtils, StringUtils } from '../../../../../common/utils';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
  },
  media: {
    width: '40%',
  },
  content: {
    flex: '1',
    marginLeft: theme.spacing(1),
  },
  title: {
    fontWeight: 'bold',
  },
  subtitle: {
    marginBottom: theme.spacing(2),
  },
  label: {
    fontWeight: 'bold',
  },
}));

function ActivityCard(props) {
  const [t] = useTranslation();
  const classes = useStyles();

  const {
    activityId,
    title,
    subtitle,
    imageUrl,
    description,
    position,
    duration,
    type,
    started,
    lastViewed,
    profile
  } = props;

  const formatTime = time => {
    const hours = _.padStart(_.floor(time / 3600), 2, '0');
    const minutes = _.padStart(_.floor((time % 3600) / 60), 2, '0');
    const seconds = _.padStart((time % 3600) % 60, 2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  const smallImageUrl = getImgixUrl(imageUrl, { w: 400 });

  return (
    <Card id={activityId} className={classes.root} variant="outlined">
      <CardMedia
        className={classes.media}
        component="img"
        image={smallImageUrl}
        alt={description}
      />
      <CardContent className={classes.content}>
        <Typography
          id={StringUtils.makeId(activityId, 'title')}
          variant="body1"
          className={classes.title}
        >
          {title}
        </Typography>
        <Typography
          id={StringUtils.makeId(activityId, 'subtitle')}
          variant="body2"
          className={classes.subtitle}
        >
          {subtitle}
        </Typography>
        <Grid container spacing={1}>
          {!_.isNil(profile) && (
            <Grid container item xs={12}>
              <Grid item xs={5} xl={4}>
                <Typography
                  id={StringUtils.makeId(activityId, 'profileLabel')}
                  variant="body2"
                  className={classes.label}
                >
                  {`${t('user.userProfile')}:`}
                </Typography>
              </Grid>
              <Grid item xs={7} xl={8}>
                <Typography id={StringUtils.makeId(activityId, 'profileValue')} variant="body2">
                  {profile}
                </Typography>
              </Grid>
            </Grid>
          )}
          {!_.isNil(type) && (
            <Grid container item xs={12}>
              <Grid item xs={5} xl={4}>
                <Typography
                  id={StringUtils.makeId(activityId, 'typeLabel')}
                  variant="body2"
                  className={classes.label}
                >
                  {`${t('activity.activityType')}:`}
                </Typography>
              </Grid>
              <Grid item xs={7} xl={8}>
                <Typography id={StringUtils.makeId(activityId, 'typeValue')} variant="body2">
                  {type}
                </Typography>
              </Grid>
            </Grid>
          )}
          {!_.isNil(duration) && (
            <Grid container item xs={12}>
              <Grid item xs={5} xl={4}>
                <Typography
                  id={StringUtils.makeId(activityId, 'durationLabel')}
                  variant="body2"
                  className={classes.label}
                >
                  {`${t('activity.activityDuration')}:`}
                </Typography>
              </Grid>
              <Grid item xs={7} xl={8}>
                <Typography id={StringUtils.makeId(activityId, 'durationValue')} variant="body2">
                  {formatTime(duration)}
                </Typography>
              </Grid>
            </Grid>
          )}
          {!_.isNil(position) && (
            <Grid container item xs={12}>
              <Grid item xs={5} xl={4}>
                <Typography
                  id={StringUtils.makeId(activityId, 'positionLabel')}
                  variant="body2"
                  className={classes.label}
                >
                  {`${t('activity.activityPosition')}:`}
                </Typography>
              </Grid>
              <Grid item xs={7} xl={8}>
                <Typography id={StringUtils.makeId(activityId, 'positionValue')} variant="body2">
                  {formatTime(position)}
                </Typography>
              </Grid>
            </Grid>
          )}
          {!_.isNil(started) && (
            <Grid container item xs={12}>
              <Grid item xs={5} xl={4}>
                <Typography
                  id={StringUtils.makeId(activityId, 'startedLabel')}
                  variant="body2"
                  className={classes.label}
                >
                  {`${t('activity.activityStarted')}:`}
                </Typography>
              </Grid>
              <Grid item xs={7} xl={8}>
                <Typography id={StringUtils.makeId(activityId, 'startedValue')} variant="body2">
                  {DateUtils.getDateTime(started)}
                </Typography>
              </Grid>
            </Grid>
          )}
          {!_.isNil(lastViewed) && (
            <Grid container item xs={12}>
              <Grid item xs={5} xl={4}>
                <Typography
                  id={StringUtils.makeId(activityId, 'lastViewedLabel')}
                  variant="body2"
                  className={classes.label}
                >
                  {`${t('activity.activityLastViewed')}:`}
                </Typography>
              </Grid>
              <Grid item xs={7} xl={8}>
                <Typography id={StringUtils.makeId(activityId, 'lastViewedValue')} variant="body2">
                  {DateUtils.getDateTime(lastViewed)}
                </Typography>
              </Grid>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
}

ActivityCard.defaultProps = {
  description: undefined,
  position: undefined,
  duration: undefined,
  type: undefined,
  started: undefined,
  lastViewed: undefined,
  profile: undefined,
};

ActivityCard.propTypes = {
  activityId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  imageUrl: PropTypes.string.isRequired,
  description: PropTypes.string,
  position: PropTypes.number,
  duration: PropTypes.number,
  type: PropTypes.string,
  started: PropTypes.string,
  lastViewed: PropTypes.string,
  profile: PropTypes.string,
};

export default ActivityCard;

function getImgixUrl(url: string, params: URLSearchParams | Record<string, string | number> = {}) {
  if (!url) {
    return url;
  }
  let u = null;
  try {
    u = new URL(url);
  } catch (ignored) {
    // Invalid URL, ignore
    return url;
  }

  const search = new URLSearchParams(u.search ?? '');

  for (const [key, value] of Object.entries(params)) {
    search.set(key, value);
  }

  u.search = search;

  return u.toString();
}
